import React, { useState, useRef } from 'react';
import { Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEditor, EditorContent } from '@tiptap/react';
import { Bold, Italic, List, ListOrdered, Image as ImageIcon, Link as LinkIcon, AlignLeft, AlignCenter, AlignRight, Quote, Code, Undo, Redo } from 'lucide-react';
interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
}
const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  placeholder = 'Start writing your article...'
}) => {
  const [linkUrl, setLinkUrl] = useState('');
  const [showLinkMenu, setShowLinkMenu] = useState(false);
  const linkInputRef = useRef<HTMLInputElement>(null);
  const editor = useEditor({
    extensions: [StarterKit],
    content: content,
    onUpdate: ({
      editor
    }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-lg max-w-none focus:outline-none min-h-[300px] px-4 py-2',
        placeholder: placeholder
      }
    }
  });
  if (!editor) {
    return null;
  }
  const addLink = () => {
    if (linkUrl) {
      editor.chain().focus().extendMarkRange('link').setLink({
        href: linkUrl
      }).run();
      setLinkUrl('');
      setShowLinkMenu(false);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && showLinkMenu) {
      e.preventDefault();
      addLink();
    }
  };
  return <div className="border border-gray-300 rounded-md overflow-hidden" data-id="element-386">
      <div className="bg-gray-50 border-b border-gray-300 p-2 flex flex-wrap gap-1" data-id="element-387">
        <button onClick={() => editor.chain().focus().toggleBold().run()} className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bold') ? 'bg-gray-200' : ''}`} title="Bold" data-id="element-388">
          <Bold size={18} data-id="element-389" />
        </button>
        <button onClick={() => editor.chain().focus().toggleItalic().run()} className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('italic') ? 'bg-gray-200' : ''}`} title="Italic" data-id="element-390">
          <Italic size={18} data-id="element-391" />
        </button>
        <button onClick={() => editor.chain().focus().toggleHeading({
        level: 1
      }).run()} className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('heading', {
        level: 1
      }) ? 'bg-gray-200' : ''}`} title="Heading 1" data-id="element-392">
          <div size={18} data-id="element-393" />
        </button>
        <button onClick={() => editor.chain().focus().toggleHeading({
        level: 2
      }).run()} className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('heading', {
        level: 2
      }) ? 'bg-gray-200' : ''}`} title="Heading 2" data-id="element-394">
          <div size={18} data-id="element-395" />
        </button>
        <button onClick={() => editor.chain().focus().toggleBulletList().run()} className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bulletList') ? 'bg-gray-200' : ''}`} title="Bullet List" data-id="element-396">
          <List size={18} data-id="element-397" />
        </button>
        <button onClick={() => editor.chain().focus().toggleOrderedList().run()} className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('orderedList') ? 'bg-gray-200' : ''}`} title="Numbered List" data-id="element-398">
          <ListOrdered size={18} data-id="element-399" />
        </button>
        <button onClick={() => editor.chain().focus().toggleBlockquote().run()} className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('blockquote') ? 'bg-gray-200' : ''}`} title="Quote" data-id="element-400">
          <Quote size={18} data-id="element-401" />
        </button>
        <button onClick={() => editor.chain().focus().setTextAlign('left').run()} className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({
        textAlign: 'left'
      }) ? 'bg-gray-200' : ''}`} title="Align Left" data-id="element-402">
          <AlignLeft size={18} data-id="element-403" />
        </button>
        <button onClick={() => editor.chain().focus().setTextAlign('center').run()} className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({
        textAlign: 'center'
      }) ? 'bg-gray-200' : ''}`} title="Align Center" data-id="element-404">
          <AlignCenter size={18} data-id="element-405" />
        </button>
        <button onClick={() => editor.chain().focus().setTextAlign('right').run()} className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({
        textAlign: 'right'
      }) ? 'bg-gray-200' : ''}`} title="Align Right" data-id="element-406">
          <AlignRight size={18} data-id="element-407" />
        </button>
        <button onClick={() => setShowLinkMenu(!showLinkMenu)} className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('link') || showLinkMenu ? 'bg-gray-200' : ''}`} title="Add Link" data-id="element-408">
          <LinkIcon size={18} data-id="element-409" />
        </button>
        <button onClick={() => editor.chain().focus().toggleCodeBlock().run()} className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('codeBlock') ? 'bg-gray-200' : ''}`} title="Code Block" data-id="element-410">
          <Code size={18} data-id="element-411" />
        </button>
        <div className="ml-auto flex" data-id="element-412">
          <button onClick={() => editor.chain().focus().undo().run()} className="p-2 rounded hover:bg-gray-200" disabled={!editor.can().undo()} title="Undo" data-id="element-413">
            <Undo size={18} className={!editor.can().undo() ? 'opacity-50' : ''} data-id="element-414" />
          </button>
          <button onClick={() => editor.chain().focus().redo().run()} className="p-2 rounded hover:bg-gray-200" disabled={!editor.can().redo()} title="Redo" data-id="element-415">
            <Redo size={18} className={!editor.can().redo() ? 'opacity-50' : ''} data-id="element-416" />
          </button>
        </div>
      </div>
      {showLinkMenu && <div className="bg-gray-50 border-b border-gray-300 p-2 flex" data-id="element-417">
          <input ref={linkInputRef} type="url" value={linkUrl} onChange={e => setLinkUrl(e.target.value)} onKeyDown={handleKeyDown} placeholder="Enter URL" className="flex-1 px-2 py-1 border border-gray-300 rounded-l-md focus:outline-none focus:ring-1 focus:ring-orange" autoFocus data-id="element-418" />
          <button onClick={addLink} className="px-3 py-1 bg-navy text-white rounded-r-md hover:bg-navy-800" data-id="element-419">
            Add
          </button>
          <button onClick={() => setShowLinkMenu(false)} className="ml-2 px-2 py-1 border border-gray-300 rounded-md hover:bg-gray-200" data-id="element-420">
            Cancel
          </button>
        </div>}
      <EditorContent editor={editor} className="min-h-[300px]" data-id="element-421" />
    </div>;
};
export default RichTextEditor;